'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import NextImage from 'next/image';
import PropTypes from 'prop-types';

import { BLOCKS } from '@contentful/rich-text-types';

import ButtonCTA from 'components/ui/ButtonCTA';
import ButtonCircle from 'components/ui/ButtonCircle';
import RichText from 'components/ui/RichText';
import Text from 'components/ui/Text';

import { blockRootProps, getColor } from 'utils';
import { addProtocol } from 'utils/url';

import styles from './BlockTestimonialCarousel.module.scss';

const testimonialOverrides = {
    renderNode: {
        [BLOCKS.PARAGRAPH]: (_node, children) => (
            <Text
                as="p"
                baseTheme="bodyMedium"
                themes={{ large: 'bodyLarge' }}
                className={styles.quote}
            >
                {children}
            </Text>
        ),
    },
};

const BlockTestimonialCarousel = ({
    __typename,
    headline,
    cta,
    icon,
    backgroundColor,
    flushTop,
    flushBottom,
    slides,
}) => {
    const slidesRef = useRef(null);
    const isScrollingFromButtonRef = useRef(false);
    const scrollTimeoutRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const container = slidesRef.current;
        if (!container) return;

        const updateIndex = () => {
            if (isScrollingFromButtonRef.current) return;
            const slideWidth = container.clientWidth;
            setCurrentIndex(Math.round(container.scrollLeft / slideWidth));
        };

        updateIndex();
        container.addEventListener('scroll', updateIndex);
        return () => container.removeEventListener('scroll', updateIndex);
    }, []);

    const handleNavigation = direction => {
        const container = slidesRef.current;
        if (container && slides?.length > 1) {
            const slideWidth = container.clientWidth;
            const fromIndex = Math.round(container.scrollLeft / slideWidth);
            const nextIndex = Math.max(
                0,
                Math.min(slides.length - 1, fromIndex + direction)
            );

            isScrollingFromButtonRef.current = true;
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = setTimeout(() => {
                isScrollingFromButtonRef.current = false;
            }, 500);

            setCurrentIndex(nextIndex);
            container.scrollTo({
                left: nextIndex * slideWidth,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        return () => {
            clearTimeout(scrollTimeoutRef.current);
        };
    }, []);

    return (
        <section
            style={
                backgroundColor
                    ? { '--background-color': getColor(backgroundColor) }
                    : undefined
            }
            {...blockRootProps(undefined, __typename)}
            className={cx(styles.root, {
                [styles.flushTop]: flushTop,
                [styles.flushBottom]: flushBottom,
            })}
        >
            <div className={styles.inner}>
                <div className={styles.heading}>
                    <div className={styles.headingLockup}>
                        {icon?.url && (
                            <NextImage
                                className={styles.icon}
                                src={addProtocol(icon.url)}
                                width={icon.width}
                                height={icon.height}
                                alt={icon.description || icon.title || ''}
                            />
                        )}
                        {headline && (
                            <Text
                                as="h2"
                                baseTheme="displaySmall"
                                themes={{ large: 'displayMedium' }}
                                className={styles.headline}
                            >
                                {headline}
                            </Text>
                        )}
                    </div>
                    {cta && <ButtonCTA {...cta} />}
                </div>
                <div className={styles.divider} aria-hidden="true" />
                <div className={styles.carousel}>
                    <div className={styles.slides} ref={slidesRef}>
                        {slides?.map((slide, index) => (
                            <article className={styles.slide} key={index}>
                                {slide.testimonial && (
                                    <RichText
                                        richText={slide.testimonial}
                                        overrides={testimonialOverrides}
                                    />
                                )}
                                {(slide.name || slide.state) && (
                                    <Text
                                        as="p"
                                        baseTheme="labelMedium"
                                        themes={{ large: 'labelLarge' }}
                                        className={styles.byline}
                                    >
                                        {[slide.name, slide.state]
                                            .filter(Boolean)
                                            .join(' | ')}
                                    </Text>
                                )}
                            </article>
                        ))}
                    </div>
                    {slides?.length > 1 && (
                        <div className={styles.navigation}>
                            <ButtonCircle
                                className={styles.navigationButton}
                                iconClassName={styles.navigationButtonIcon}
                                theme="small-fill-blue"
                                iconType="chevronLeft"
                                onClick={() => handleNavigation(-1)}
                                disabled={currentIndex === 0}
                                autoComplete="off"
                            />
                            <ButtonCircle
                                className={styles.navigationButton}
                                iconClassName={styles.navigationButtonIcon}
                                theme="small-fill-blue"
                                iconType="chevronRight"
                                onClick={() => handleNavigation(1)}
                                disabled={currentIndex >= slides.length - 1}
                                autoComplete="off"
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

BlockTestimonialCarousel.propTypes = {
    __typename: PropTypes.string,
    headline: PropTypes.string,
    cta: PropTypes.object,
    icon: PropTypes.object,
    backgroundColor: PropTypes.string,
    flushTop: PropTypes.bool,
    flushBottom: PropTypes.bool,
    slides: PropTypes.array,
};

export default BlockTestimonialCarousel;
